import api from './api';

// Legacy schema-by-name lookup (kept this release for ManualExecution +
// LocationInfoTab). Backend route was renamed from /actions to
// /action-schemas to free /actions for the new CRUD endpoints.
const fetchActionDetails = async (action, inputType = 'input') => {
  try {
    const response = await api.get(
      `/action-schemas?filter[action]=${action}&filter[type]=${inputType}`,
    );
    const { data } = response.data;

    return { data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

// Legacy category-keyed action listing; still backs the TriggerFilter
// dropdown. The new CRUD list endpoint (`fetchActions` below) is preferred
// for the Toolkit Actions UI.
const fetchCategoriesAndActions = async () => {
  try {
    const response = await api.get('/categories-and-actions');
    const { data } = response.data;

    return { data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

// New DB-backed Actions endpoints (PD-2651).

const fetchActions = async ({
  categoryUuid = null,
  locked = null,
  latest = null,
  page = null,
  limit = null,
} = {}) => {
  try {
    const params = {
      'filter[category_uuid]': categoryUuid,
      'filter[locked]': locked,
      'filter[latest]': latest,
      'page[number]': page,
      'page[size]': limit,
    };
    const filtered = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== null),
    );
    const response = await api.get('/actions', { params: filtered });
    return { data: response.data.data, meta: response.data.meta };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchActionDetail = async (uuid) => {
  try {
    const response = await api.get(`/actions/${uuid}`);
    return { data: response.data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const createAction = async (attributes) => {
  try {
    const response = await api.post('/actions', {
      data: { type: 'actions', attributes },
    });
    return { data: response.data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const updateAction = async (
  uuid,
  attributes,
  { acknowledgeBreakingChange = false } = {},
) => {
  try {
    const payload = { data: { type: 'actions', attributes } };
    if (acknowledgeBreakingChange) {
      payload.data.meta = { acknowledge_breaking_change: true };
    }
    const response = await api.patch(`/actions/${uuid}`, payload);
    return { data: response.data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors, status: e.response?.status };
  }
};

const deleteAction = async (uuid) => {
  try {
    await api.delete(`/actions/${uuid}`, { data: {} });
    return { ok: true };
  } catch (e) {
    return { errors: e.response?.data?.errors, status: e.response?.status };
  }
};

const createActionVersion = async (sourceUuid, attributes) => {
  try {
    const response = await api.post(`/actions/${sourceUuid}/versions`, {
      data: { type: 'action_versions', attributes },
    });
    return { data: response.data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchActionIntegrations = async (
  uuid,
  { includeDiscarded = false } = {},
) => {
  try {
    const params = {};
    if (includeDiscarded) params['filter[include_discarded]'] = 'true';
    const response = await api.get(`/actions/${uuid}/integrations`, { params });
    return { data: response.data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const bulkMigrateIntegrations = async (
  sourceUuid,
  { targetUuid, integrationUuids },
) => {
  try {
    const response = await api.post(`/actions/${sourceUuid}/migrate`, {
      data: {
        type: 'action_migrations',
        attributes: {
          target_uuid: targetUuid,
          integration_uuids: integrationUuids,
        },
      },
    });
    return { data: response.data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors, status: e.response?.status };
  }
};

const lockAction = async (uuid) => {
  try {
    const response = await api.post(`/actions/${uuid}/lock`, {
      data: { type: 'action_locks', attributes: {} },
    });
    return { data: response.data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const unlockAction = async (uuid) => {
  try {
    const response = await api.post(`/actions/${uuid}/unlock`, {
      data: { type: 'action_locks', attributes: {} },
    });
    return { data: response.data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

// Environment sync (PD-3396): export returns the full JSON:API document
// verbatim (meta + data + included) so it round-trips straight into import.
const exportAction = async (uuid) => {
  try {
    const response = await api.get(`/actions/${uuid}/export`);
    return { data: response.data };
  } catch (e) {
    return { errors: e.response?.data?.errors, status: e.response?.status };
  }
};

// `document` is the whole JSON:API document (as exported); post it as-is.
const importAction = async (document) => {
  try {
    const response = await api.post('/actions/import', document);
    return { data: response.data.data.attributes, status: response.status };
  } catch (e) {
    return { errors: e.response?.data?.errors, status: e.response?.status };
  }
};

export default {
  fetchActionDetails,
  fetchCategoriesAndActions,
  // Back-compat alias: TriggerFilter still imports `fetchActions` and
  // expects the legacy category-keyed payload shape.
  fetchActions: fetchCategoriesAndActions,
  // PD-2651 DB-backed Actions API:
  fetchActionsList: fetchActions,
  fetchActionDetail,
  createAction,
  updateAction,
  deleteAction,
  createActionVersion,
  fetchActionIntegrations,
  bulkMigrateIntegrations,
  lockAction,
  unlockAction,
  exportAction,
  importAction,
};
